
import { message } from 'antd';
import React from 'react';
import * as THREE from 'three';
import { Vector3 } from 'three';
import { Sidebar } from './Sidebar';
import { PanoramaBackground } from '../Renderer/PanoramaBackground';
import LoadPointCloudModal from '../LoadPointCloudModal';
import PhotoSphere from '../utils/PhotoSphere';
import { lengthUnitFromProjection } from '../utils/pointCloudUnits';

// import vanillaJS Potree libs, /!\ would be best with proper ES6 import
// @ts-ignore
const Potree = window.Potree;
// @ts-ignore
const TWEEN = window.TWEEN;

enum CameraMode {
    FREE_CAM,
    PHOTO_SPHERE
}

/** Which way a photo sphere view faces, in degrees: compass heading (0 = north, clockwise) and tilt (0 = level, up is positive). */
export interface Facing {
    heading: number,
    tilt: number,
}

/** How long the view must stay still before its facing is reported. */
const FACING_SETTLE_MS = 500;

// A look-at point is faced a little down, low on a post standing
// there (as Sign Review's own sphere viewer does), so a sign and the foot of
// its post are both in view. The camera rides on the car's roof, taken as this
// far above the ground (the point cloud is not searched for it): the nearer
// the sphere, the further down, but never more than the limit.
const CAMERA_HEIGHT_M = 2.5;
const AIM_HEIGHT_M = 1;
const MAX_AIM_DOWN_RAD = (20 * Math.PI) / 180;

interface Props {
    mapRef: React.RefObject<any>,
    modalRef: React.RefObject<LoadPointCloudModal>,
    ref: React.RefObject<any>,
    data_identifier?: string,
    getAccessTokenSilently: () => Promise<string>,
    activeSatelliteView: boolean,
    toggleSatelliteView: () => void,
    /** Called when a photo sphere view settles on a new facing (e.g. to keep it in the URL) */
    onFacingChange?: (facing: Facing) => void,
}

interface State {
    cameraMode: CameraMode
    activePanorama: boolean
    photoSphere: PhotoSphere | null
}

export default class PointCloudWindow extends React.Component<Props, State> {
    potreeContainerDiv: any;
    viewer: any;
    guiLoaded = false;
    pointCloudCenter: Vector3;
    // The photo sphere's photo shown behind the point cloud (tracked so its
    // texture can be disposed when it is replaced or hidden).
    currentPanorama: PanoramaBackground | null = null;
    // The photo sphere whose photo is loading; a slower, older load is dropped.
    loadingPanoramaFor: PhotoSphere | null = null;
    // Last loadPointCloud args, reused to recover after a WebGL context loss.
    lastLoadArgs: { rootUrl: string; projection?: string } | null = null;
    // True while we intentionally tear down the old renderer (forceContextLoss), so
    // the context-lost handler doesn't treat our own disposal as a crash.
    intentionalDispose = false;
    // Timestamp of the last context-restore reload, to avoid a reload loop.
    lastRestoreReload = 0;

    constructor(props: Props) {
        super(props);

        this.state = {
            cameraMode: CameraMode.FREE_CAM,
            activePanorama: false,
            photoSphere: null
        };

        this.potreeContainerDiv = React.createRef();

        this.pointCloudCenter = new Vector3(0, 0, 0);
    }

    async loadPointCloud(rootUrl: string, projection?: string) {
        console.debug("Beginning to load point cloud!");
        this.lastLoadArgs = { rootUrl, projection };

        // clean up old potree viewer
        if (this.viewer) {
            // use Three.js WebGLRenderer dispose and force context loss to clean up old viewer
            this.intentionalDispose = true;
            if (this.viewer.renderer) {
                this.viewer.renderer.dispose();
                this.viewer.renderer.forceContextLoss();
            }
            this.viewer = null;
            this.currentPanorama = null;
            this.intentionalDispose = false;

            // remove potree container
            const container = this.potreeContainerDiv.current;
            if (container) {
                container.innerHTML = '';
            }
        }

        // initialize Potree viewer
        const viewerElem = this.potreeContainerDiv.current;
        this.viewer = new Potree.Viewer(viewerElem);

        const viewer = this.viewer;
        // Recover from WebGL context loss instead of leaving a permanent white/black canvas.
        this.attachContextLossHandlers(viewer?.renderer?.domElement);
        this.watchFacing(viewer);

        viewer.setEDLEnabled(true);
        viewer.setFOV(60);
        viewer.setPointBudget(1_000_000);
        viewer.setClipTask(Potree.ClipTask.SHOW_INSIDE);
        viewer.loadSettingsFromURL();
        viewer.useHQ = true;
        this.enterFreeCamMode();
        // Length unit from the cloud's proj4 projection (authoritative). Fall back
        // to the legacy URL heuristic only when the projection is unavailable.
        const legacyUrlUnit =
            rootUrl.includes('minessota') || rootUrl.includes('montana') ||
            rootUrl.includes('minnesota') || rootUrl.includes('NAVVIS')
                ? 'm'
                : 'ft';
        const lengthUnit = lengthUnitFromProjection(projection) ?? legacyUrlUnit;
        viewer.setLengthUnitAndDisplayUnit(lengthUnit, 'in');


        if (this.guiLoaded) {
            document.getElementById("sidebar_root")?.remove();
            this.guiLoaded = false;
        }

        if (!this.guiLoaded) {
            viewer.loadGUI(() => {
                viewer.setLanguage('en');
                $("#menu_appearance").next().show();
                // viewer.toggleSidebar();
            });

            this.guiLoaded = true;
        }
        // Load and add point cloud to scene
        try {
            // Handles case where there is no data identifier
            if (!this.props.data_identifier) {
                throw new Error('Data identifier is required to load point cloud.');
            }
            
        const url = rootUrl + '/metadata.json';
        return await Potree.loadPointCloud(url).then((e: any) => {
            console.debug("Loaded PC succesfully!", e);
            const pointcloud = e.pointcloud;
            const material = pointcloud.material;
            // Grab the center
            // TODO actually get the true center
            this.pointCloudCenter = pointcloud.position;


            // material.activeAttributeName = "rgba";
            material.minSize = 2;
            material.size = 5;
            viewer.scene.addPointCloud(pointcloud);
            viewer.fitToScreen();
            // fitToScreen moves the camera through a TWEEN even with no duration,
            // so the move only happens on the next rendered frame. Finish it now:
            // a photo sphere opened before that frame (e.g. from an Asset Editor
            // link) would otherwise be turned toward the cloud's center afterwards.
            // The page loads tween.js 0.15, which has no TWEEN.now(); it times
            // tweens with performance.now().
            TWEEN.update(window.performance.now() + 1);

            // Return the pointcloud object so we can access its bounding box
            return pointcloud;
        }, (e: any) => {
            console.error("ERROR: ", e);
            throw e;
        });
    } catch (error) {
        console.error('Error loading point cloud:', error);
        throw error;
    }
    }

    moveToPosition(orientation: any, latLng: any, updateLocation: any) {
        const view = this.viewer.scene.view;
        // a vector 3 containing x y and z

        const position = new Vector3(orientation.x, orientation.y, orientation.z);
        const camera = this.viewer.scene.getActiveCamera();
        let positiveAngle = orientation.heading * (Math.PI / 180);
        positiveAngle = positiveAngle % 360;
        positiveAngle = positiveAngle > 0 ? positiveAngle : positiveAngle + 360;

        updateLocation(latLng, 360 - orientation.heading);
        const radiansToRotate = THREE.MathUtils.degToRad(360 - orientation.heading);

        const heading = new Vector3(0, 0, radiansToRotate);

        view.position.copy(position);
        view.lookAt(heading);
        view.position.copy(position);
    }

    addPhotoSpheres(locations: Vector3[]) {
        for (const loc of locations) {
            const geometry = new THREE.SphereGeometry(0.25, 10, 10);
            const material_new = new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0.2 });
            const sphere = new THREE.Mesh(geometry, material_new);
            sphere.position.set(loc.x, loc.y, loc.z);
            this.viewer.scene.scene.add(sphere);
        }

    }

    componentDidMount() {
        (window as any).showCopySuccess = (text: string) => {
            message.success(text);
        };
        // Sidebar owns Standard-measurement copy (see attachStandardListeners) so it
        // can copy correctly across the vertical/non-vertical toggle. Disable
        // Potree's native drop-copy, whose label-visibility gate goes stale on that
        // toggle (double-copy or missed copy).
        (window as any).__doppelSidebarOwnsCopy = true;
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
        if (prevState.activePanorama !== this.state.activePanorama) {
            if (this.state.activePanorama) {
                this.loadPanorama(this.state.photoSphere as PhotoSphere);
            } else {
                this.clearSceneBackground();
            }

        }
        if (prevState.photoSphere !== this.state.photoSphere) {
            this.loadPanorama(this.state.photoSphere as PhotoSphere);
        }

    }

    enterPhotoSphereMode() {
        const viewer = this.viewer;
        viewer.setControls(viewer.orbitControls);
        this.setState({ cameraMode: CameraMode.PHOTO_SPHERE });

        viewer.setEDLOpacity(0.25);
    }

    enterFreeCamMode() {
        const viewer = this.viewer;
        // Turn on earth controls from viewer
        viewer.setControls(viewer.earthControls);
        // Update camera mode in state
        this.setState({ cameraMode: CameraMode.FREE_CAM, activePanorama: false });
        // Remove our background image we use for orbit mode
        this.clearSceneBackground();
        // Make our opacity full again.
        viewer.setEDLOpacity(1);
    }

    exitFreeCamMode() {
        if (this.state.photoSphere) {
            this.jumpToPhotoSphere(this.state.photoSphere);
        }
    }

    toggleFreeCam = () => {
        if (this.state.cameraMode === CameraMode.FREE_CAM) {
            this.exitFreeCamMode();
        } else {
            this.enterFreeCamMode();
        }
    };

    /**
     * @description Reports the photo sphere view's facing once it settles,
     * whether the user turned it or a jump did. Potree's yaw is 0 facing +y
     * (north) and grows counter-clockwise, so the compass heading is -yaw.
     * @param viewer The Potree viewer to watch
     */
    watchFacing(viewer: any) {
        let last = { yaw: NaN, pitch: NaN };
        let timer: ReturnType<typeof setTimeout> | undefined;
        viewer.addEventListener('update', () => {
            if (this.state.cameraMode !== CameraMode.PHOTO_SPHERE || !this.state.photoSphere) return;
            const { yaw, pitch } = viewer.scene.view;
            if (Math.abs(yaw - last.yaw) < 1e-4 && Math.abs(pitch - last.pitch) < 1e-4) return;
            last = { yaw, pitch };
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => {
                // A viewer replaced meanwhile (another point cloud) reports nothing.
                if (this.viewer !== viewer) return;
                const degrees = (radians: number) => (radians * 180) / Math.PI;
                this.props.onFacingChange?.({
                    heading: Math.round(((-degrees(yaw) % 360) + 360) % 360),
                    tilt: Math.round(degrees(pitch)),
                });
            }, FACING_SETTLE_MS);
        });
    }

    /**
     * @description Turns the current photo sphere view to a saved facing.
     * @param facing Compass heading and tilt, in degrees
     */
    applyFacing(facing: Facing) {
        const view = this.viewer?.scene.view;
        if (!view) return;
        view.yaw = (-facing.heading * Math.PI) / 180;
        view.pitch = (facing.tilt * Math.PI) / 180;
        this.notifyCameraMoved();
    }

    /**
     * @description Tells the map where the view is and which way it faces, so
     * its arrow follows. Built from the view itself: the active camera only
     * takes the view's rotation on the next rendered frame, so reading it right
     * after a programmatic move gave the previous direction.
     */
    notifyCameraMoved() {
        const view = this.viewer?.scene.view;
        if (!view) return;
        const freeCamMoveEvent = new CustomEvent("on-free-cam-moved", {
            detail: {
                position: view.position,
                // Same layout Potree gives the camera: x tilts, z turns (yaw)
                rotation: new THREE.Euler(Math.PI / 2 + view.pitch, 0, view.yaw, 'ZXY'),
            },
        });
        document.getElementById("event_listener")?.dispatchEvent(freeCamMoveEvent);
    }

    /**
     * @description Views a photo sphere. By default the view faces the way the
     * sphere was captured; a look-at point (e.g. a sign another app asked
     * about) turns it toward that point instead, a little down toward the
     * lower part of a post standing there.
     * Stepping from one sphere to another can keep the current facing instead,
     * so moving along the street feels like walking forward.
     * @param photoSphere The sphere to view
     * @param lookAt A world position to face, if any
     * @param keepFacing Keep the current facing when already in a sphere
     */
    jumpToPhotoSphere(photoSphere: PhotoSphere, lookAt?: Vector3, keepFacing = false) {
        // Only a sphere view has a facing worth keeping; free cam may be looking
        // down at the whole cloud.
        const view = this.viewer.scene.view;
        const kept = keepFacing && !lookAt && this.state.cameraMode === CameraMode.PHOTO_SPHERE ?
            { yaw: view.yaw, pitch: view.pitch } :
            undefined;

        // Are we in photosphere mode?
        this.setState({ photoSphere })
        if (this.state.cameraMode != CameraMode.PHOTO_SPHERE) {
            this.enterPhotoSphereMode();
        }
        // Aim at the look-at's post (see AIM_HEIGHT_M), in the cloud's units,
        // and ignore one that sits on the camera, which has no direction.
        let facing: Vector3 | undefined;
        if (lookAt) {
            const unitsPerMeter = this.viewer.lengthUnit?.unitspermeter > 0 ? this.viewer.lengthUnit.unitspermeter : 1;
            const level = new Vector3(lookAt.x, lookAt.y, photoSphere.camera_pos.z);
            const across = level.distanceTo(photoSphere.camera_pos);
            const down = Math.min((CAMERA_HEIGHT_M - AIM_HEIGHT_M) * unitsPerMeter, across * Math.tan(MAX_AIM_DOWN_RAD));
            facing = level.setZ(photoSphere.camera_pos.z - down);
        }
        const target = facing && facing.distanceTo(photoSphere.camera_pos) > 0.01 ? facing : photoSphere.camera_target;
        // Goto axis
        view.position.copy(photoSphere.camera_pos);
        // Look at position
        view.lookAt(target);
        // Goto position
        view.position.copy(photoSphere.camera_pos);
        // Anchor to axis. Orbit controls turn the view around a pivot `radius`
        // in front of the camera, so the camera circles that far as it turns:
        // keep the sphere's own short radius even when facing a far look-at,
        // or the camera (and the map arrow) drifts off the sphere.
        view.radius = -1 * Math.abs(photoSphere.camera_pos.distanceTo(photoSphere.camera_target));
        if (kept) {
            view.yaw = kept.yaw;
            view.pitch = kept.pitch;
        }

        this.setState({ activePanorama: true })

        this.notifyCameraMoved();
    }


    /**
     * Attach WebGL context-loss handlers to the renderer canvas. Without these a
     * lost context leaves a permanently white/black canvas; preventDefault lets the
     * browser restore it, and on restore we reload the cloud (its GPU buffers are gone).
     */
    attachContextLossHandlers(canvas: HTMLCanvasElement | undefined) {
        if (!canvas) return;
        canvas.addEventListener('webglcontextlost', (e: Event) => {
            // Required for the browser to attempt a restore instead of killing the canvas.
            e.preventDefault();
            if (this.intentionalDispose) return; // our own teardown, not a crash
            if (this.viewer?.renderer?.domElement !== canvas) return; // stale canvas
            console.warn('[PointCloudWindow] WebGL context lost');
            message.warning('3D view interrupted (graphics memory) — restoring…');
        });
        canvas.addEventListener('webglcontextrestored', () => {
            if (this.viewer?.renderer?.domElement !== canvas) return; // stale canvas
            const now = Date.now();
            if (this.lastRestoreReload && now - this.lastRestoreReload < 8000) {
                console.warn('[PointCloudWindow] context restored again too soon — not reloading');
                message.error('3D view keeps losing graphics memory — please reload the page.');
                return;
            }
            this.lastRestoreReload = now;
            console.warn('[PointCloudWindow] WebGL context restored — reloading point cloud');
            if (this.lastLoadArgs) {
                this.loadPointCloud(this.lastLoadArgs.rootUrl, this.lastLoadArgs.projection);
            }
        });
    }

    /**
     * Dispose the photo shown behind the point cloud before replacing or hiding
     * it, so its texture's GPU memory is freed instead of building up across
     * photo spheres and free-cam toggles until the WebGL context is lost.
     */
    clearSceneBackground() {
        if (this.currentPanorama) {
            try { this.currentPanorama.dispose(); } catch { /* ignore */ }
            this.currentPanorama = null;
        }
        if (this.viewer?.scene?.scene) this.viewer.scene.scene.background = null;
    }

    /**
     * @description Shows a photo sphere's photo behind the point cloud. The photo
     * is drawn as it is, lined up with the cloud (see PanoramaBackground), rather
     * than converted into a six-sided cube map first: it looks the same, uses
     * ~380 MB less GPU memory for an 8192 x 4096 photo and skips the conversion.
     * @param photoSphere The photosphere we are loading.
     */
    async loadPanorama(photoSphere: PhotoSphere) {
        const viewer = this.viewer;
        if (!viewer) return;

        // Dispose the current photo before loading a new one.
        this.clearSceneBackground();
        this.loadingPanoramaFor = photoSphere;

        try {
            // Begin loading our panoramic texture stored in our aws folder
            const texture = new THREE.TextureLoader().load(photoSphere.panorama_image, () => {
                // Another sphere was opened (or the viewer replaced) meanwhile.
                if (!this.viewer || this.viewer !== viewer || this.loadingPanoramaFor !== photoSphere) {
                    texture.dispose();
                    return;
                }
                const panorama = new PanoramaBackground(texture, photoSphere.initial_rotation);
                // Never picked by clicks in the cloud.
                panorama.mesh.raycast = () => undefined;
                this.clearSceneBackground();
                this.currentPanorama = panorama;
                // Drawn first in the scene, before the point cloud is laid over it.
                viewer.scene.scene.add(panorama.mesh);
            });
        } catch (error) {
            console.error('Error loading the panorama image:', error);
        }
    }

    getPointCloudBoundingBox() {
        return this.viewer.scene.getBoundingBox(); // Assuming there's only one point cloud
    }

    togglePanorama() {
        this.setState({ activePanorama: !this.state.activePanorama })
    }
    /**
     * @returns {Vector3} The center of the point cloud
     */
    getPointCloudCenter() {
        return this.pointCloudCenter;
    }

    takeScreenShot() {
        const canvases = document.querySelectorAll(' #potree_render_area canvas');

        const visibleCanvases = Array.from(canvases).filter((canvas) => {
            const style = window.getComputedStyle(canvas);

            return style.display !== 'none';
        });

        // Now visibleCanvases contains all the canvas elements that are visible
        if (visibleCanvases.length > 0) {
            const canvas = visibleCanvases[visibleCanvases.length - 1];
            // @ts-ignore
            const imageDataUrl = canvas.toDataURL('image/png');
            const downloadLink = document.createElement('a');
            downloadLink.href = imageDataUrl;
            const date = 'Date: ' + new Date().toLocaleDateString() + ', Time: ' + new Date().toLocaleTimeString().replace(/:/g, '-').replace(/ /g, ' ');

            downloadLink.download = `${date}.png`;
            downloadLink.click();
        }
    }
    render() {
        return (
            <>
                {/* New React Sidebar */}
                {this.viewer && (
                    <Sidebar
                        viewer={this.viewer}
                        modalRef={this.props.modalRef}
                        toggleFreeCam={this.toggleFreeCam}
                        canEnterFreeCam={!!this.state.photoSphere}
                        takeScreenShot={() => this.takeScreenShot()}
                        togglePanorama={() => this.togglePanorama()}
                        activePanorama={this.state.activePanorama}
                        activeFreeCam={this.state.cameraMode === CameraMode.FREE_CAM}
                        activeSatelliteView={this.props.activeSatelliteView}
                        toggleSatelliteView={this.props.toggleSatelliteView}
                    />
                )}
                <div className="potree_container" id="potree-root">
                    <div id="potree_render_area" ref={this.potreeContainerDiv} />
                    <div id="potree_sidebar_container" />
                </div>
            </>
        );
    }
}
